import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import { Textarea } from "../../../ui/textarea";
import { ArrowLeft, Eye, Loader2 } from "lucide-react";
import VisualEmailBuilder, {
  renderDocumentToHtml,
  type EmailDocument,
} from "../email-builder/VisualEmailBuilder";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { useToast } from "../../../../hooks/use-toast";

const TemplateEditor = () => {
  const { id } = useParams<{ id: string }>();
  console.log(id,'newiddddd')
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { merchant } = useMerchant();
  const { toast } = useToast();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [bodyJson, setBodyJson] = useState<EmailDocument | null>(null);
  const [htmlDirty, setHtmlDirty] = useState(false);
  const [visualBuilderKey, setVisualBuilderKey] = useState(0);
  const [editorTab, setEditorTab] = useState<"visual" | "html">(
    (searchParams.get("tab") as "visual" | "html") || "visual"
  );
  const [type, setType] = useState<string>("manual");

  const load = useCallback(async () => {
    if (isNew || !merchant) return;
    setLoading(true);
    const { data, error } = await epicengageSupabasev1
      .from("email_templates")
      .select("name, subject_line, type, body_html, body_json")
      .eq("id", id!)
      .maybeSingle();
    if (error) {
      toast({ title: "Failed to load template", description: error.message, variant: "destructive" });
    } else if (data) {
      setName(data.name ?? "");
      setSubject(data.subject_line ?? "");
      setBody(data.body_html ?? "");
      setType(data.type ?? "manual");
      if (data.body_json && typeof data.body_json === "object") {
        setBodyJson(data.body_json as EmailDocument);
        setHtmlDirty(false);
        setEditorTab("visual");
      } else {
        setBodyJson(null);
        setHtmlDirty(Boolean(data.body_html?.trim()));
        setEditorTab("html");
      }
    }
    setLoading(false);
  }, [id, isNew, merchant, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const htmlToVisualDocument = (html: string): EmailDocument => {
    const trimmed = html.trim();
    let contents = trimmed;
    try {
      const parsed = new DOMParser().parseFromString(trimmed, "text/html");
      contents = parsed.body?.innerHTML?.trim() || trimmed;
    } catch {
      contents = trimmed;
    }

    return {
      root: {
        type: "EmailLayout",
        data: {
          backdropColor: "#F5F5F5",
          canvasColor: "#FFFFFF",
          textColor: "#262626",
          fontFamily: "MODERN_SANS",
          childrenIds: ["html-content"],
        },
      },
      "html-content": {
        type: "Html",
        data: {
          props: { contents: contents || "<p></p>" },
          style: {
            fontSize: 16,
            textAlign: null,
            padding: { top: 24, bottom: 24, left: 24, right: 24 },
          },
        },
      },
    };
  };

  const handleEditorTabChange = (value: string) => {
    const nextTab = value as "visual" | "html";
    if (nextTab === "visual" && body.trim() && (!bodyJson || htmlDirty)) {
      setBodyJson(htmlToVisualDocument(body));
      setHtmlDirty(false);
      setVisualBuilderKey((key) => key + 1);
    }
    setEditorTab(nextTab);
  };

  const handleSave = async () => {
    if (!merchant) return;
    if (!name.trim()) return toast({ title: "Name required", variant: "destructive" });
    if (!subject.trim()) return toast({ title: "Subject line required", variant: "destructive" });

    setSaving(true);
    const isVisual = editorTab === "visual" && bodyJson;
    const body_html = isVisual ? renderDocumentToHtml(bodyJson as EmailDocument) : body;
    const body_plain = body_html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const payload = {
      merchant_id: merchant.id,
      name: name.trim(),
      subject_line: subject.trim(),
      body_html,
      body_json: isVisual ? ((bodyJson as unknown) as never) : null,
      body_plain,
      type,
    };

    const res = isNew
      ? await epicengageSupabasev1.from("email_templates").insert(payload).select("id").single()
      : await epicengageSupabasev1.from("email_templates").update(payload).eq("id", id!);

    setSaving(false);
    if (res.error) {
      toast({ title: "Save failed", description: res.error.message, variant: "destructive" });
      return;
    }
    toast({ title: isNew ? "Template created" : "Template updated" });
    navigate("/clientpanel/enagev1/templates");
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
    );
  }

  return (
      <div className="px-10 py-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/clientpanel/enagev1/templates")}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {isNew ? "Create template" : "Edit template"}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {isNew ? "Build a reusable email template" : "Update your email template"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (
              <Button variant="outline" onClick={() => navigate(`/clientpanel/enagev1/templates/${id}/preview`)}>
                <Eye className="h-4 w-4" />
                Preview
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isNew ? "Create template" : "Save changes"}
            </Button>
          </div>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="tpl-name">Template Name</Label>
            <Input
              id="tpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Welcome Email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tpl-subject">Subject Line</Label>
            <Input
              id="tpl-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Hey {{first_name}}, welcome!"
            />
            <p className="text-xs text-muted-foreground">
              Tip: use <code className="px-1 py-0.5 bg-muted rounded">{`{{first_name}}`}</code> to personalize
            </p>
          </div>

          <div className="space-y-2">
            <Label>Email Body</Label>
            <Tabs value={editorTab} onValueChange={handleEditorTabChange}>
              <TabsList>
                <TabsTrigger value="visual">Visual Builder</TabsTrigger>
                <TabsTrigger value="html">HTML</TabsTrigger>
              </TabsList>
              <TabsContent value="visual" className="mt-2">
                <VisualEmailBuilder
                  key={visualBuilderKey}
                  value={bodyJson}
                  onChange={(doc, html) => {
                    setBodyJson(doc);
                    setBody(html);
                    setHtmlDirty(false);
                  }}
                  height="70vh"
                />
              </TabsContent>
              <TabsContent value="html" className="mt-2">
                <Textarea
                  value={body}
                  onChange={(e) => {
                    setBody(e.target.value);
                    setBodyJson(null);
                    setHtmlDirty(true);
                  }}
                  className="font-mono text-xs"
                  rows={24}
                  placeholder="<html>..."
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
  );
};

export default TemplateEditor;
